import React, { useEffect, useState } from "react";
import { Settings2, LogOut, User as UserIcon, Skull, BookOpen } from "lucide-react";
import { AudioSettings } from "./AudioSettings";

const BG = "https://images.pexels.com/photos/4012056/pexels-photo-4012056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ENDING_LABEL = {
  requiem: "REQUIEM",
  memory: "MEMORY",
  vengeance: "VENGEANCE",
  open: "THE OPEN DOOR",
};

export const MainMenu = ({ hasSave, meta, auth, onStart, onContinue, onCredits, onHowToPlay }) => {
  const [glitching, setGlitching] = useState(false);
  const [audOpen, setAudOpen] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      setGlitching(true);
      setTimeout(() => setGlitching(false), 220);
    }, 5200);
    return () => clearInterval(id);
  }, []);

  const endingsSeen = meta?.endings_seen || [];
  const ngPlus = meta?.ng_plus;

  return (
    <div className="relative min-h-screen w-full overflow-hidden" data-testid="main-menu">
      <div className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG})`, filter: "brightness(0.32) saturate(0.6) contrast(1.2)" }} aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/95" aria-hidden />

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col justify-between px-8 md:px-16 lg:px-24 py-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-emergency animate-heartbeat" aria-hidden />
              <span className="font-mono text-xs tracking-[0.4em] uppercase text-white/50">Orpheus Biomedical // Blackwood Annex</span>
            </div>
            {(auth?.user || auth?.guest) && (
              <div className="flex items-center gap-4" data-testid="menu-user-badge">
                <div className="flex items-center gap-2 text-white/70">
                  {auth.guest ? <Skull size={13} className="text-emergency" /> : <UserIcon size={13} className="text-emergency" />}
                  <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
                    {auth.user ? auth.user.username : "GUEST"}
                  </span>
                </div>
                <button
                  onClick={auth.logout}
                  className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-emergency transition-colors flex items-center gap-1"
                  data-testid="menu-logout"
                  title={auth.guest ? "Return to sign-in" : "Sign out"}
                >
                  <LogOut size={11} />
                  {auth.guest ? "SIGN IN" : "SIGN OUT"}
                </button>
              </div>
            )}
          </div>

          <div className="max-w-2xl">
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-white/40 mb-4" data-testid="menu-subtitle">
              A NARRATIVE HORROR — HALLOWEEN EVE, 11:17 PM
            </p>
            <h1
              className={`horror-title text-5xl md:text-7xl lg:text-8xl leading-[0.95] mb-3 ${glitching ? "animate-glitch" : ""}`}
              data-testid="game-title"
            >
              Dark Requiem
            </h1>
            <h2 className="horror-title not-italic text-2xl md:text-3xl lg:text-4xl text-white/60 mb-10">
              <span className="text-white/30">— </span>
              <span className="accent">The Corruption</span>
              <span className="text-white/30"> Within</span>
            </h2>

            <p className="narrative text-white/60 max-w-lg mb-12 text-sm md:text-base">
              Jack Mercer, 14. His father David worked at a facility that officially does not exist.
              His mother Miriam died there six years ago in an accident nobody was allowed to describe.
              Tonight, something inside the building remembers Jack&apos;s name before he does.
            </p>

            <div className="flex flex-col gap-3 max-w-md">
              {hasSave && (
                <button className="term-btn" onClick={onContinue} data-testid="continue-btn">
                  CONTINUE — where you left him
                </button>
              )}
              <button className="term-btn term-btn-danger" onClick={onStart} data-testid="new-game-btn">
                {ngPlus ? "NEW GAME + — the Entity remembers" : hasSave ? "BEGIN AGAIN — forget everything" : "ENTER THE FACILITY"}
              </button>
              <button className="term-btn" onClick={onCredits} data-testid="about-btn">
                ABOUT — a note from the developer
              </button>
              <button className="term-btn" onClick={onHowToPlay} data-testid="howtoplay-btn">
                <span className="inline-flex items-center gap-3"><BookOpen size={14} /> HOW TO PLAY — briefing &amp; controls</span>
              </button>
              <button className="term-btn" onClick={() => setAudOpen(true)} data-testid="menu-audio-btn">
                <span className="inline-flex items-center gap-3"><Settings2 size={14} /> AUDIO — volume, music, narration</span>
              </button>
            </div>

            {endingsSeen.length > 0 && (
              <div className="mt-10 border border-white/10 p-5 max-w-md bg-black/50" data-testid="endings-panel">
                <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/40 mb-3">// ENDINGS WITNESSED — {endingsSeen.length} / 4</p>
                <ul className="space-y-1">
                  {["requiem", "memory", "vengeance", "open"].map((k) => (
                    <li key={k} className={`font-mono text-xs ${endingsSeen.includes(k) ? "text-emergency" : "text-white/25"}`}>
                      {endingsSeen.includes(k) ? "●" : "○"} {ENDING_LABEL[k]}
                      {k === "open" && !endingsSeen.includes(k) && <span className="text-white/20 ml-2 italic">(secret — NG+)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/25 flex items-center gap-4">
            <span>v1.0 — pre-release</span>
            <span className="text-white/15">|</span>
            <span>headphones recommended</span>
            <span className="text-white/15">|</span>
            <span>lights off</span>
          </div>
        </div>

        <div className="hidden lg:flex items-center justify-center relative">
          <div className="w-[2px] h-2/3 bg-gradient-to-b from-transparent via-emergency/40 to-transparent" />
        </div>
      </div>
      <AudioSettings open={audOpen} onClose={() => setAudOpen(false)} />
    </div>
  );
};
